import { forwardRef, type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { PaginationButton } from './PaginationButton';
import { getVisiblePages } from './visiblePages';
import type { PaginationProps } from './tableTypes';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Pagination component
 */
export const Pagination = forwardRef<
  HTMLDivElement,
  PaginationProps & HTMLAttributes<HTMLDivElement>
>(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      showPageNumbers = true,
      maxVisiblePages = 5,
      className,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const visiblePages = getVisiblePages(currentPage, totalPages, maxVisiblePages);

    return (
      <div
        ref={ref}
        className={cn('flex items-center justify-center gap-1', 'py-3 px-4', className)}
        role="navigation"
        aria-label={t('a11y.pagination')}
        {...props}
      >
        {/* Previous button */}
        <PaginationButton
          page={currentPage - 1}
          onPageChange={onPageChange}
          disabled={currentPage <= 1}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </PaginationButton>

        {/* Page numbers */}
        {showPageNumbers && visiblePages.length > 0 && (
          <div className="flex items-center gap-1 mx-2">
            {visiblePages[0]! > 1 && (
              <>
                <PaginationButton page={1} onPageChange={onPageChange}>
                  1
                </PaginationButton>
                {visiblePages[0]! > 2 && <span className="px-2 text-surface-500">...</span>}
              </>
            )}

            {visiblePages.map((page) => (
              <PaginationButton
                key={page}
                page={page}
                onPageChange={onPageChange}
                active={page === currentPage}
              >
                {page}
              </PaginationButton>
            ))}

            {visiblePages[visiblePages.length - 1]! < totalPages && (
              <>
                {visiblePages[visiblePages.length - 1]! < totalPages - 1 && (
                  <span className="px-2 text-surface-500">...</span>
                )}
                <PaginationButton page={totalPages} onPageChange={onPageChange}>
                  {totalPages}
                </PaginationButton>
              </>
            )}
          </div>
        )}

        {/* Next button */}
        <PaginationButton
          page={currentPage + 1}
          onPageChange={onPageChange}
          disabled={currentPage >= totalPages}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </PaginationButton>
      </div>
    );
  }
);

Pagination.displayName = 'Pagination';
